import { useEffect, useState } from 'react';
import { useClient } from 'urql';

import { FeatureFlag } from '@revelio/core';
import { RICS_SELECTION_LIST } from '@revelio/data-access';

import { useKibanaLogger } from '../../hooks/kibana-logger/kibana-logger';
import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import { fetchSelectionListsFromAPI } from '../api/fetchSelectionLists';
import type { SelectionListContextType } from '../SelectionListProvider';
import { standardGqlSelectionListKeys } from '../types';
import {
  getSelectionListsFromStore,
  setSelectionListStore,
} from '../utils/cache';
import { sanitizeSelectionLists } from '../utils/validation';

export const useInitializeSelectionLists = ({
  dispatch,
}: {
  dispatch: SelectionListContextType['dispatch'];
}) => {
  const gqlClient = useClient();
  const { kibanaLogger } = useKibanaLogger();
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);

  const [selectionListsError, setSelectionListsError] = useState<Error | null>(
    null
  );
  const [selectionListsLoading, setSelectionListsLoading] = useState(false);

  useEffect(() => {
    if (!gqlClient) return;
    let fullDataDispatched = false;

    // Fire lightweight RICS query in parallel — may already be cached from
    // landing-page prefetch. This unblocks adaptive taxonomy resolution on
    // company pages without waiting for the full selection list.
    gqlClient
      .query(RICS_SELECTION_LIST, {})
      .toPromise()
      .then(({ data }) => {
        if (fullDataDispatched) return; // full data already set, no need
        const ricsK400 = data?.selectionList?.rics_k400?.filter(
          (item): item is NonNullable<typeof item> => item != null
        );
        if (ricsK400?.length) {
          dispatch({
            type: 'MERGE_SELECTION_LISTS',
            payload: { rics_k400: ricsK400 },
          });
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {}); // best-effort — full list fetch is the fallback

    (async () => {
      // loading: true
      setSelectionListsLoading(true);

      /** Selection List IndexedDB Store */
      const selectionListsStore = await getSelectionListsFromStore(
        standardGqlSelectionListKeys,
        { referenceListsMerged: useReferenceApi }
      );

      /** if found in store, set the state and return */
      if (selectionListsStore) {
        fullDataDispatched = true;
        dispatch({ type: 'SET_SELECTION_LISTS', payload: selectionListsStore });
        setSelectionListsLoading(false);
        return;
      }

      /** Fetch data from API */
      const { data: selectionLists, error: selectionListsError } =
        await fetchSelectionListsFromAPI(gqlClient, useReferenceApi);

      if (selectionListsError) setSelectionListsError(selectionListsError);
      const { data: sanitized, strippedKeys } =
        sanitizeSelectionLists(selectionLists);

      if (strippedKeys.length > 0) {
        kibanaLogger?.warn(
          `SelectionLists API returned null fields: ${strippedKeys.join(', ')}`,
          { context: 'selection-lists-partial-data' }
        );
      }

      if (sanitized) {
        fullDataDispatched = true;
        dispatch({ type: 'SET_SELECTION_LISTS', payload: sanitized });
        await setSelectionListStore(sanitized, {
          referenceListsMerged: useReferenceApi,
        });
      }

      setSelectionListsLoading(false);
    })();
  }, [gqlClient, dispatch, kibanaLogger, useReferenceApi]);

  return { selectionListsError, selectionListsLoading };
};
